import { useTranslation } from "react-i18next";

import { FilterBar } from "@/components/filter-bar";
import { FilterToggle } from "@/components/filter-toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  hasRoadmapFilters,
  PRIORITY_FILTERS,
  type PriorityFilter,
  priorityLabel,
  ROADMAP_TOGGLES,
  type RoadmapFilters,
  type RoadmapToggle,
} from "./utils";

interface RoadmapFiltersBarProps {
  filters: RoadmapFilters;
  onChange: (filters: RoadmapFilters) => void;
  /** Clears every filter, the search included. */
  onReset: () => void;
  /** How many items each toggle would show (`toggleCounts`). */
  counts: Record<RoadmapToggle, number>;
  /** A developer's search also finds Linear ids, and its placeholder says so. */
  isDeveloper: boolean;
}

/**
 * The filters above the roadmap: one row (`<FilterBar>`) with a search on
 * title and summary (and Linear ids for a developer), the priority and the
 * reset, whose place is kept while no filter is set; under it a row of three
 * toggles, Geblokkeerd, Wacht op mij and Gevolgd door mij, each with its
 * count. On a row of their own the toggles never squeeze the search, and they
 * wrap on a phone.
 */
export function RoadmapFiltersBar({
  filters,
  onChange,
  onReset,
  counts,
  isDeveloper,
}: RoadmapFiltersBarProps) {
  const { t } = useTranslation();
  const set = (change: Partial<RoadmapFilters>) => onChange({ ...filters, ...change });

  return (
    <div className="space-y-2">
      <FilterBar
        search={{
          value: filters.search,
          onChange: (search) => set({ search }),
          label: isDeveloper ? t("roadmap.filter.searchDeveloper") : t("roadmap.filter.search"),
        }}
        columns="narrow"
        filtered={hasRoadmapFilters(filters)}
        onReset={onReset}
      >
        <Select
          value={filters.priority}
          onValueChange={(priority) => set({ priority: priority as PriorityFilter })}
        >
          <SelectTrigger size="sm" aria-label={t("roadmap.filter.priority")} className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRIORITY_FILTERS.map((priority) => (
              <SelectItem key={priority} value={priority}>
                {priority === "all" ? t("roadmap.filter.allPriorities") : priorityLabel(priority)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterBar>
      <div role="group" aria-label={t("roadmap.filter.toggles")} className="flex flex-wrap gap-2">
        {ROADMAP_TOGGLES.map((toggle) => (
          <FilterToggle
            key={toggle}
            label={t(`roadmap.filter.${toggle}`)}
            count={counts[toggle]}
            pressed={filters[toggle]}
            onPressedChange={(pressed) => set({ [toggle]: pressed })}
          />
        ))}
      </div>
    </div>
  );
}
